import * as React from 'react'
import { useState, useRef, useEffect } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import { Building2, Building, Users, Users2, LogOut, ChevronDown, User, CalendarDays, FileUp, LayoutGrid, Check } from 'lucide-react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@/features/auth/AuthContext'
import { ROLE_LABELS } from '@/features/auth/types'
import { Avatar } from '@/components/primitives/Avatar'
import { AppSwitcher } from '@/components/layout/AppSwitcher'
import { listFacilities } from '@/features/facilities/api'
import { useFacilityFilter } from '@/store/facilityFilterStore'
import './ManageBar.css'

const THINKADMIN_NAV = [
  { to: '/admin/organizations', label: 'Organizations', icon: Building, end: false },
  { to: '/admin/facilities',    label: 'Facilities',    icon: Building2, end: false },
  { to: '/admin/users',         label: 'Users',         icon: Users, end: false },
]

const SUPER_ADMIN_NAV = [
  { to: '/residents', label: 'Residents', icon: Users2, end: false },
]

const NURSE_NAV = [
  { to: '/residents', label: 'Residents', icon: Users2, end: false },
]

/** Compact facility switcher for SUPER_ADMIN — dropdown to select active facility. */
function FacilitySwitcher(): React.JSX.Element | null {
  const { facilityId, setFacility } = useFacilityFilter()
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  const { data: facilities = [] } = useQuery({
    queryKey: ['facilities-list'],
    queryFn: listFacilities,
    staleTime: 30_000,
  })

  useEffect(() => {
    if (!open) return undefined
    function onClick(e: MouseEvent): void {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  if (facilities.length === 0) return null
  const selected = facilities.find((f) => f.id === facilityId) ?? null

  function handleSelect(id: string | null): void {
    setFacility(id)
    void queryClient.invalidateQueries({ queryKey: ['residents'] })
    setOpen(false)
  }

  return (
    <div className="managebar-facility-switcher" ref={ref}>
      <button
        type="button"
        className="managebar-facility-trigger"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        {selected ? <Building2 size={13} strokeWidth={1.9} /> : <LayoutGrid size={13} strokeWidth={1.9} />}
        <span className="managebar-facility-name">{selected ? selected.name : 'All Facilities'}</span>
        <ChevronDown size={12} strokeWidth={2} style={{ opacity: 0.7, transform: open ? 'rotate(180deg)' : undefined, transition: 'transform 0.15s' }} />
      </button>

      {open ? (
        <div className="managebar-facility-dropdown" role="listbox">
          <button
            type="button"
            role="option"
            aria-selected={facilityId === null}
            className={'managebar-facility-option' + (!facilityId ? ' managebar-facility-option--active' : '')}
            onClick={() => handleSelect(null)}
          >
            <span className={'managebar-facility-icon' + (!facilityId ? ' managebar-facility-icon--active' : '')}>
              <LayoutGrid size={13} />
            </span>
            <span className="managebar-facility-option-text">
              <span className="managebar-facility-option-name">All Facilities</span>
              <span className="managebar-facility-option-sub">{facilities.length} facilities</span>
            </span>
            {!facilityId && <Check size={13} />}
          </button>
          <div className="managebar-facility-divider" />
          {facilities.map((f) => {
            const isActive = facilityId === f.id
            return (
              <button
                key={f.id}
                type="button"
                role="option"
                aria-selected={isActive}
                className={'managebar-facility-option' + (isActive ? ' managebar-facility-option--active' : '')}
                onClick={() => handleSelect(f.id)}
              >
                <span className={'managebar-facility-icon' + (isActive ? ' managebar-facility-icon--active' : '')}>
                  <Building2 size={13} />
                </span>
                <span className="managebar-facility-option-text">
                  <span className="managebar-facility-option-name">{f.name}</span>
                  {(f.city ?? f.state) ? (
                    <span className="managebar-facility-option-sub">{[f.city, f.state].filter(Boolean).join(', ')}</span>
                  ) : null}
                </span>
                {isActive && <Check size={13} />}
              </button>
            )
          })}
        </div>
      ) : null}
    </div>
  )
}

/** Non-interactive chip showing the user's fixed facility name. */
export function FacilityChip(): React.JSX.Element | null {
  const { user } = useAuth()
  const { data: facilities = [] } = useQuery({
    queryKey: ['facilities-list'],
    queryFn: listFacilities,
    staleTime: 60_000,
    enabled: !!user?.facility_id,
  })
  const facility = facilities.find((f) => f.id === user?.facility_id) ?? null
  if (!facility) return null
  return (
    <span className="managebar-facility-chip">
      <Building2 size={13} strokeWidth={1.9} />
      {facility.name}
    </span>
  )
}

interface ManageBarProps {
  rightSlot?: React.ReactNode
  onOpenSchedule?: () => void
  onOpenImport?: () => void
}

export function ManageBar({ rightSlot, onOpenSchedule, onOpenImport }: ManageBarProps = {}): React.JSX.Element {
  const { user, logout, hasRole } = useAuth()
  const navigate = useNavigate()
  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!menuOpen) return undefined
    function onClick(e: MouseEvent): void {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false)
      }
    }
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') setMenuOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [menuOpen])

  async function onLogout(): Promise<void> {
    await logout()
    navigate('/login', { replace: true })
  }

  const navItems = hasRole('THINKADMIN')
    ? THINKADMIN_NAV
    : hasRole('SUPER_ADMIN')
    ? SUPER_ADMIN_NAV
    : NURSE_NAV

  return (
    <header className="managebar">
      {/* Brand */}
      <div className="managebar-left">
        <img src="/vitals-logo.svg" alt="eCareLTC Vitals" className="managebar-logo" />
      </div>

      {/* Tab nav */}
      <nav className="managebar-nav">
        {navItems.map(({ to, label, icon: Icon, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            className={({ isActive }) =>
              'managebar-link' + (isActive ? ' managebar-link-active' : '')
            }
          >
            <Icon size={15} strokeWidth={1.9} />
            {label}
          </NavLink>
        ))}
      </nav>

      {/* Right side */}
      <div className="managebar-right">
        {hasRole('SUPER_ADMIN') ? <FacilitySwitcher /> : null}
        {!hasRole('THINKADMIN') && !hasRole('SUPER_ADMIN') ? <FacilityChip /> : null}
        <AppSwitcher />
        {rightSlot}
        {user != null && (
          <div className="managebar-user-menu" ref={menuRef}>
            <button
              type="button"
              className="managebar-user-trigger"
              onClick={() => setMenuOpen((v) => !v)}
              aria-expanded={menuOpen}
              aria-haspopup="menu"
            >
              <Avatar name={user.name} size="sm" gradient />
              <span className="managebar-user">
                <span className="managebar-user-name">{user.name}</span>
                <span className="managebar-user-role">{ROLE_LABELS[user.role]}</span>
              </span>
              <ChevronDown size={12} strokeWidth={2} className={'managebar-chevron' + (menuOpen ? ' managebar-chevron-open' : '')} />
            </button>

            {menuOpen ? (
              <div className="managebar-menu" role="menu">
                <button
                  type="button"
                  className="managebar-menu-item"
                  onClick={() => { setMenuOpen(false); navigate('/profile') }}
                >
                  <User size={14} strokeWidth={1.8} />
                  Profile
                </button>
                {(onOpenSchedule ?? onOpenImport) ? <div className="managebar-menu-divider" /> : null}
                {onOpenSchedule ? (
                  <button
                    type="button"
                    className="managebar-menu-item"
                    onClick={() => { setMenuOpen(false); onOpenSchedule() }}
                  >
                    <CalendarDays size={14} strokeWidth={1.8} />
                    Schedule settings
                  </button>
                ) : null}
                {onOpenImport ? (
                  <button
                    type="button"
                    className="managebar-menu-item"
                    onClick={() => { setMenuOpen(false); onOpenImport() }}
                  >
                    <FileUp size={14} strokeWidth={1.8} />
                    Import residents (CSV)
                  </button>
                ) : null}
                <div className="managebar-menu-divider" />
                <button
                  type="button"
                  className="managebar-menu-item managebar-menu-danger"
                  onClick={() => void onLogout()}
                >
                  <LogOut size={14} strokeWidth={1.8} />
                  Sign out
                </button>
              </div>
            ) : null}
          </div>
        )}
      </div>
    </header>
  )
}
